import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { setAppLang } from "@/i18n/appLang";
import { Download, Loader2, Smartphone } from "lucide-react";
import { DevistoLogo } from "@/components/DevistoLogo";
import { PublicQuestion } from "@/components/studio/PublicQuestion";
import { API, beDate, eur, btnPrimary, btnGhost, setCurrency } from "@/lib/studioApi";

export default function PublicInvoice() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/facture/${token}`).then(({ data }) => { setCurrency(data.invoice?.currency); setAppLang(data.invoice?.lang); setData(data); }).catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <main className="font-body min-h-screen bg-[#F1F5F9] flex items-center justify-center px-6">
        <p className="text-sm text-[#5C6A87]">Ce document n'est plus disponible. Contactez votre entrepreneur.</p>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="font-body min-h-screen bg-[#F1F5F9] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#3A5CC8]" />
      </main>
    );
  }

  const { company, invoice, lines } = data;
  const balance = Number(invoice.total) - Number(invoice.paid_amount || 0);

  return (
    <main className="font-body min-h-screen bg-[#F1F5F9] px-4 py-8 sm:py-14">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-3xl border border-[#E2E7F7] bg-white p-6 sm:p-10 shadow-[0_1px_3px_rgba(15,38,69,0.06)]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              {company.logo_media_id && (
                <img src={`${API}/media/${company.logo_media_id}`} alt={company.name} className="mb-2 h-12 w-auto max-w-[200px] object-contain object-left" />
              )}
              <p className="font-heading text-xl font-bold text-[#0F2645]">{company.name}</p>
              <p className="mt-2 text-xs text-[#5C6A87] leading-relaxed">
                {company.address} {company.postal_code} {company.city}<br />
                {company.phone} {company.email && `· ${company.email}`}<br />
                {company.vat && `TVA ${company.vat}`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#3A5CC8]">{invoice.kind_label}</p>
              <p data-testid="public-invoice-reference" className="font-heading text-2xl font-bold text-[#0F2645]">{invoice.reference}</p>
              <p className="mt-1 text-xs text-[#5C6A87]">Date : {beDate(invoice.issue_date)}</p>
              <p className="text-xs text-[#5C6A87]">Échéance : {beDate(invoice.due_date)}</p>
              {invoice.quote_number && <p className="text-xs text-[#5C6A87]">Devis : {invoice.quote_number}</p>}
            </div>
          </div>

          <p className="mt-8 text-sm font-semibold text-[#0F2645]">Client : {invoice.client_name}</p>

          <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E2E7F7]">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#F6F8FD] text-[11px] uppercase tracking-wide text-[#5C6A87]">
                <tr>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-2 py-3 text-right">Qté</th>
                  <th className="px-2 py-3 text-right">P.U.</th>
                  <th className="px-2 py-3 text-right">TVA</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t border-[#E2E7F7]">
                    <td className="px-4 py-3 text-[#0F2645]">{l.description}</td>
                    <td className="px-2 py-3 text-right text-[#5C6A87]">{l.quantity} {l.unit}</td>
                    <td className="px-2 py-3 text-right text-[#5C6A87]">{eur(l.unit_price)}</td>
                    <td className="px-2 py-3 text-right text-[#5C6A87]">{l.reverse_charge ? "AC" : `${l.vat_percent} %`}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#0F2645]">{eur(Number(l.quantity) * Number(l.unit_price))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5 space-y-1 text-right text-sm">
            <p className="text-[#5C6A87]">Total HTVA : <span className="font-semibold text-[#0F2645]">{eur(invoice.net)}</span></p>
            <p className="text-[#5C6A87]">TVA : <span className="font-semibold text-[#0F2645]">{eur(invoice.vat_total)}</span></p>
            <p className="font-heading text-2xl font-bold text-[#0F2645]">{eur(invoice.total)}</p>
            {Number(invoice.paid_amount) > 0 && (
              <p className="text-sm font-semibold text-green-700">Déjà payé {eur(invoice.paid_amount)} · solde {eur(balance)}</p>
            )}
          </div>

          {invoice.notes && <p className="mt-6 text-sm text-[#5C6A87] leading-relaxed">{invoice.notes}</p>}
          {invoice.reverse_charge_mention && <p className="mt-4 text-xs font-semibold text-[#0F2645]">{invoice.reverse_charge_mention}</p>}
          {company.iban && (
            <p className="mt-4 text-sm text-[#5C6A87]">
              Paiement sur le compte <span className="font-semibold text-[#0F2645]">{company.iban}</span> avec la communication <span className="font-semibold text-[#0F2645]">{invoice.reference}</span>.
            </p>
          )}
          {invoice.conditions && <p className="mt-4 text-xs text-[#5C6A87] leading-relaxed">{invoice.conditions}</p>}
          {data.cgv && (
            <details data-testid="public-cgv" className="mt-4 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-3 text-xs text-[#5C6A87]">
              <summary className="cursor-pointer font-semibold text-[#0F2645]">{data.cgv.title}</summary>
              <div className="mt-2 space-y-1 leading-relaxed">
                {data.cgv.text.split("\n").map((line, i) => (/^(article|artikel|artículo|art\.|#)/i.test(line.trim())
                  ? <p key={i} className="pt-2 font-semibold text-[#0F2645]">{line.replace(/^#\s*/, "")}</p>
                  : <p key={i}>{line}</p>))}
              </div>
            </details>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {company.iban && invoice.kind !== "avoir" && Number(invoice.total) - Number(invoice.paid_amount || 0) > 0.5 ? (
              <Link to={`/payer/${token}`} data-testid="public-invoice-pay" className={btnPrimary}>
                <Smartphone className="w-4 h-4" /> Payer avec mon app bancaire
              </Link>
            ) : null}
            <a href={`${API}/facture/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="public-invoice-pdf" className={company.iban ? btnGhost : btnPrimary}>
              <Download className="w-4 h-4" /> Télécharger le PDF
            </a>
          </div>
        </div>

        <PublicQuestion path={`/facture/${token}`} docLabel={invoice.kind === "avoir" ? "cette note de crédit" : "cette facture"} defaultName={invoice.client_name || ""} />

        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-[#93A0BC]">
          Document émis avec <DevistoLogo className="h-4 w-auto opacity-60" />
        </p>
      </div>
    </main>
  );
}
